import { ReactNode } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  type DialogProps,
  DialogTitle,
} from "@mui/material";
import shiColors from "../theme/shiColors";
import { SaveButton } from "./SaveButton";

interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  description: ReactNode;
  children: ReactNode;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  confirmDisabled?: boolean;
  maxWidth?: DialogProps["maxWidth"];
  destructive?: boolean;
  savingText?: string;
}

/**
 * A flexible, composable confirmation dialog component.
 *
 * Handles the dialog shell, button layout, and loading states while letting
 * consumers provide their own description and content via ReactNode props.
 *
 * @param open - Controls dialog visibility
 * @param onClose - Called when the dialog is closed (cancel button or backdrop click)
 * @param onConfirm - Called when the confirm button is clicked
 * @param title - Dialog title (default: "Are you sure?")
 * @param description - Descriptive text shown below the title, e.g. "You are about to [ACTION]"
 * @param children - Content area for custom content (item details, forms, etc.)
 * @param confirmText - Text for the confirm button (default: "Confirm")
 * @param cancelText - Text for the cancel button (default: "Cancel")
 * @param isLoading - Shows spinner on confirm button and disables both buttons
 * @param maxWidth - MUI Dialog max width preset: "xs" | "sm" | "md" | "lg" | "xl" | false (default: "sm")
 * @param destructive - Use red confirm button for destructive actions like delete/unallocate (default: false)
 *
 * @example
 * ```tsx
 * <ConfirmationDialog
 *   open={isOpen}
 *   title="Are you sure?"
 *   description={<>You are about to <strong>delete</strong> this item:</>}
 *   confirmText="Yes, Delete"
 *   cancelText="No"
 *   isLoading={isLoading}
 *   destructive
 *   onClose={handleClose}
 *   onConfirm={handleConfirm}
 * >
 *   <Typography variant="body2">Item: Example Item Name</Typography>
 * </ConfirmationDialog>
 * ```
 */
const ConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  title = "Are you sure?",
  description,
  children,
  confirmText = "Confirm",
  cancelText = "Cancel",
  isLoading = false,
  confirmDisabled = false,
  maxWidth = "sm",
  destructive = false,
  savingText = undefined,
}: ConfirmationDialogProps) => {
  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      fullWidth
      maxWidth={maxWidth}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <DialogContentText component="div" sx={{ mb: 2 }}>
          {description}
        </DialogContentText>
        {children}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={onClose}
            disabled={isLoading}
            sx={{ color: shiColors.gray.light }}
            variant="text"
          >
            {cancelText}
          </Button>
          <SaveButton
            onClick={onConfirm}
            disabled={isLoading || confirmDisabled}
            variant="text"
            color={destructive ? "error" : "primary"}
            saveText={confirmText}
            savingText={savingText}
            isSaving={isLoading}
          ></SaveButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
